import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContextBackend';

export default function ImpersonationCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processImpersonation = async () => {
      // Extract impersonation params from URL
      const sessionToken = searchParams.get('impersonation_token');
      const sessionId = searchParams.get('impersonation_session_id');
      const expiresAt = searchParams.get('impersonation_expires_at');
      const reason = searchParams.get('impersonation_reason');

      // Get admin token from sessionStorage (set by admin-portal before redirect).
      // Both apps are on the same origin (apps.traineryhcm.com) so sessionStorage is shared.
      // Do NOT pass in URL — the JWT is ~1.5KB and causes 431 errors from CloudFront.
      const adminToken = sessionStorage.getItem('impersonation_admin_token');
      if (adminToken) {
        sessionStorage.removeItem('impersonation_admin_token');
      }

      console.log('👤 ImpersonationCallback received params:', {
        sessionToken: sessionToken ? `${sessionToken.substring(0, 20)}...` : 'MISSING',
        sessionId: sessionId || 'MISSING',
        expiresAt: expiresAt || 'MISSING',
        reason: reason || 'MISSING',
        adminToken: adminToken ? `${adminToken.substring(0, 20)}...` : 'MISSING (check sessionStorage)',
      });

      if (!sessionToken || !sessionId || !expiresAt || !adminToken) {
        console.error('❌ Missing impersonation parameters:', {
          hasSessionToken: !!sessionToken,
          hasSessionId: !!sessionId,
          hasExpiresAt: !!expiresAt,
          hasAdminToken: !!adminToken,
        });
        setError(adminToken ? 'Missing impersonation parameters' : 'Admin token not found. Please try again from the admin portal.');
        setIsProcessing(false);
        return;
      }

      try {
        const result = await startImpersonation({
          sessionToken,
          sessionId,
          expiresAt,
          reason: reason || '',
          adminToken,
        });

        if (result.success) {
          // Verify localStorage is set before navigating
          const storedData = localStorage.getItem('impersonation_data');
          if (!storedData) {
            console.error('Impersonation data not stored in localStorage');
            setError('Failed to initialize impersonation session');
            setIsProcessing(false);
            return;
          }

          console.log('✅ Impersonation session ready, navigating to my-portal...');

          // Small delay to ensure React state catches up
          setTimeout(() => {
            navigate('/home/my-portal', { replace: true });
          }, 100);
        } else {
          setError(result.error || 'Failed to start impersonation');
          setIsProcessing(false);
        }
      } catch (err) {
        console.error('Impersonation error:', err);
        setError('An error occurred while starting impersonation');
        setIsProcessing(false);
      }
    };

    processImpersonation();
  }, [searchParams, startImpersonation, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">!</div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Impersonation Failed
            </h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => {
                window.location.href = `${import.meta.env.VITE_SHELL_APP_URL}/tnadmin/client`;
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Return to Admin Portal
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Starting Impersonation
            </h1>
            <p className="text-gray-600">
              Please wait while we set up the impersonation session...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
